<template>
  <q-layout view="lHh Lpr lFf" class="main-layout">
    <q-header elevated class="header-glass">
      <q-toolbar :dense="compactToolbar">
        <q-btn
          flat
          dense
          round
          icon="menu"
          :aria-label="t('common.menu')"
          @click="leftDrawerOpen = !leftDrawerOpen"
        />

        <q-toolbar-title :class="{ 'toolbar-brand--compact': compactToolbar }">
          <div class="text-h2 text-weight-light text-primary brand-text">Blu</div>
        </q-toolbar-title>

        <!-- Session ID for debugging (dev only) -->
        <div v-if="isDevelopment" class="session-debug" :class="compactToolbar ? 'q-ml-xs' : 'q-ml-md'">
          <template v-if="compactToolbar">
            <q-btn
              dense
              flat
              round
              color="warning"
              icon="bug_report"
              @click="showSessionDialog = true"
            >
              <q-tooltip>{{ t('layout.clickViewSession') }}</q-tooltip>
            </q-btn>
            <q-btn
              dense
              flat
              round
              color="info"
              icon="refresh"
              class="q-ml-xs"
              @click="refreshSessionInfo"
            >
              <q-tooltip>{{ t('layout.refreshSessionInfo') }}</q-tooltip>
            </q-btn>
          </template>
          <template v-else>
            <q-chip
              dense
              color="warning"
              text-color="dark"
              :label="sessionId || t('common.loading')"
              icon="bug_report"
              clickable
              @click="showSessionDialog = true"
              :title="t('layout.clickViewSession')"
            />
            <q-btn
              dense
              flat
              color="info"
              icon="refresh"
              @click="refreshSessionInfo"
              class="q-ml-sm"
              :title="t('layout.refreshSessionInfo')"
            />
          </template>
        </div>

        <!-- Terminal Connection Status Badge (hidden for gateways without status support, e.g., mxmerchant) -->
        <div class="terminal-status" :class="compactToolbar ? 'q-ml-xs' : 'q-ml-md'" v-if="supportsTerminalStatus">
          <q-chip
            dense
            :color="getTerminalStatusColor"
            text-color="white"
            :label="compactToolbar ? undefined : getTerminalStatusLabel"
            :icon="getTerminalStatusIcon"
            :loading="registerStore.terminalStatusLoading"
            @click="refreshTerminalConnection"
            clickable
            :title="t('layout.terminalRefreshTitle')"
          >
            <q-tooltip v-if="compactToolbar">{{ getTerminalStatusLabel }}</q-tooltip>
          </q-chip>
        </div>

        <!-- Current Register Display -->
        <div v-if="registerStore.isCurrentRegisterOpen" class="current-register" :class="compactToolbar ? 'q-ml-xs' : 'q-ml-md'">
          <q-chip
            color="positive"
            text-color="white"
            :label="compactToolbar ? undefined : `${registerStore.currentRegister.name}`"
            icon="point_of_sale"
            class="current-register-chip"
            :title="registerChipTitle"
            clickable
            @click="onRegisterToolbarClick"
          />
        </div>

        <q-btn
          v-if="canOrderAheadQueue"
          flat
          round
          dense
          icon="room_service"
          :class="compactToolbar ? 'q-ml-xs q-mr-xs' : 'q-ml-sm q-mr-md'"
          :aria-label="t('layout.orderAheadPickup')"
          :aria-expanded="orderAheadDrawerOpen"
          @click="orderAheadDrawerOpen = !orderAheadDrawerOpen"
        >
          <q-badge
            v-if="orderAheadStore.queueCount > 0"
            color="negative"
            rounded
            floating
          >
            {{ orderAheadStore.queueCount }}
          </q-badge>
        </q-btn>

        <q-btn
          v-if="canStoreOrderQueue"
          flat
          round
          dense
          icon="storefront"
          :class="compactToolbar ? 'q-ml-xs q-mr-xs' : 'q-ml-sm q-mr-md'"
          :aria-label="t('layout.storeOrderPickup')"
          :to="{ name: 'store-order-queue' }"
        >
          <q-badge
            v-if="storeOrderQueueStore.queueCount > 0"
            color="negative"
            rounded
            floating
          >
            {{ storeOrderQueueStore.queueCount }}
          </q-badge>
        </q-btn>

        <!-- Desktop (Electron) auto-update -->
        <div
          v-if="showElectronUpdateAffordance"
          class="electron-update-toolbar"
          :class="compactToolbar ? 'q-ml-xs' : 'q-ml-md'"
        >
          <q-btn
            v-if="electronAutoUpdate.updatePhase === 'available'"
            dense
            flat
            no-caps
            color="positive"
            icon="system_update_alt"
            :label="compactToolbar ? undefined : $t('layout.desktopUpdate.updateNow')"
            @click="electronAutoUpdate.downloadUpdate"
          >
            <q-tooltip v-if="electronAutoUpdate.updateInfo?.version">
              {{ $t('layout.desktopUpdate.versionAvailable', { version: electronAutoUpdate.updateInfo.version }) }}
            </q-tooltip>
            <q-tooltip v-else>
              {{ $t('layout.desktopUpdate.updateNow') }}
            </q-tooltip>
          </q-btn>
          <q-chip
            v-else-if="electronAutoUpdate.updatePhase === 'downloading'"
            dense
            color="info"
            text-color="white"
            icon="download"
          >
            {{ $t('layout.desktopUpdate.downloading', { percent: Math.round(electronAutoUpdate.updateProgress) }) }}
          </q-chip>
          <q-btn
            v-else-if="electronAutoUpdate.updatePhase === 'downloaded'"
            dense
            flat
            no-caps
            color="positive"
            icon="restart_alt"
            :label="compactToolbar ? undefined : $t('layout.desktopUpdate.restartToUpdate')"
            @click="electronAutoUpdate.installUpdate"
          >
            <q-tooltip>{{ $t('layout.desktopUpdate.restartToUpdate') }}</q-tooltip>
          </q-btn>
        </div>

        <location-selector />
        <user-menu :class="compactToolbar ? 'q-ml-xs' : 'q-ml-md'" />
      </q-toolbar>

      <q-banner v-if="isOffline" dense class="bg-orange-9 text-white">
        <template #avatar>
          <q-icon name="signal_wifi_off" />
        </template>
        <div class="text-weight-medium">{{ t('layout.offline') }}</div>
        <div class="text-caption">
          {{ t('layout.offlineMessage') }}
          <span v-if="effectiveType"> ({{ t('layout.offlineNetwork', { type: effectiveType }) }})</span>
        </div>
      </q-banner>
    </q-header>

    <q-drawer
      v-model="leftDrawerOpen"
      show-if-above
      class="drawer-glass"
      :width="250"
      :breakpoint="DRAWER_SIDEBAR_MIN_WIDTH"
    >
      <q-list>
        <q-item-label header class="q-pa-md">
          <div class="text-h2 text-weight-light text-primary brand-text">Blu</div>
        </q-item-label>

        <q-item
          v-for="link in links"
          :key="link.link"
          :to="link.link"
          clickable
          v-ripple
          :active="isActive(link.link)"
          class="nav-link"
        >
          <q-item-section avatar>
            <q-icon :name="link.icon" />
          </q-item-section>

          <q-item-section>
            {{ t(link.titleKey || link.title) }}
          </q-item-section>
        </q-item>

        <q-separator class="q-my-md separator-glass" />

        <q-item clickable v-ripple @click="handleLogout" class="nav-link">
          <q-item-section avatar>
            <q-icon name="logout" />
          </q-item-section>

          <q-item-section>
            {{ t('auth.logout') }}
          </q-item-section>
        </q-item>
      </q-list>
    </q-drawer>

    <q-drawer
      v-if="canOrderAheadQueue"
      v-model="orderAheadDrawerOpen"
      side="right"
      overlay
      elevated
      behavior="mobile"
      :width="orderAheadDrawerWidth"
      :breakpoint="1200"
      class="pickup-drawer"
    >
      <OrderAheadQueueDrawerPanel @close="orderAheadDrawerOpen = false" />
    </q-drawer>

    <q-page-container>
      <router-view v-slot="{ Component }">
        <component :is="Component" :key="routeViewKey" />
      </router-view>
    </q-page-container>

    <!-- Session Info Dialog -->
    <q-dialog v-model="showSessionDialog" persistent class="session-info-dialog" :maximized="$q.screen.lt.md">
      <q-card class="pos-dialog pos-dialog--lg column no-wrap">
        <q-card-section class="dialog-header--slate">
          <div class="row items-center justify-between">
            <div>
              <div class="text-h6 text-white text-weight-light">{{ t('layout.sessionInfo') }}</div>
            </div>
            <q-btn icon="close" flat round dense color="white" v-close-popup />
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="pos-dialog__body q-pt-md">
          <div class="row q-col-gutter-md">
            <!-- Session ID -->
            <div class="col-12">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.sessionId') }}</div>
                <div class="text-body2 font-mono bg-grey-2 q-pa-sm rounded">{{
                    sessionInfo.session_id || t('common.loading')
                  }}
                </div>
              </q-card>
            </div>

            <!-- User Information -->
            <div class="col-12 col-md-6">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.user') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.name') }}:</strong> {{ sessionInfo.user?.name || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.email') }}:</strong> {{ sessionInfo.user?.email || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.id') }}:</strong> {{ sessionInfo.user?.id || t('layout.none') }}</div>
                </div>
              </q-card>
            </div>

            <!-- Authentication Status -->
            <div class="col-12 col-md-6">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.authentication') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.status') }}:</strong>
                    <q-chip
                      :color="authStore.isAuthenticated ? 'positive' : 'negative'"
                      size="sm"
                      :label="authStore.isAuthenticated ? t('layout.authenticated') : t('layout.notAuthenticated')"
                    />
                  </div>
                  <div><strong>{{ t('layout.token') }}:</strong> {{ authStore.token ? t('layout.present') : t('layout.none') }}</div>
                </div>
              </q-card>
            </div>

            <!-- Location Information -->
            <div class="col-12 col-md-6">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.currentLocation') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.name') }}:</strong> {{ locationStore.getCurrentLocation?.name || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.id') }}:</strong> {{ locationStore.getCurrentLocation?.id || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.address') }}:</strong> {{ locationStore.getCurrentLocation?.address || t('layout.none') }}</div>
                </div>
              </q-card>
            </div>

            <!-- Register Information -->
            <div class="col-12 col-md-6">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.currentRegister') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.name') }}:</strong> {{ registerStore.currentRegister?.name || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.id') }}:</strong> {{ registerStore.currentRegister?.id || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.status') }}:</strong>
                    <q-chip
                      :color="registerStore.currentRegister?.is_open ? 'positive' : 'grey'"
                      size="sm"
                      :label="registerStore.currentRegister?.is_open ? t('layout.open') : t('layout.closed')"
                    />
                  </div>
                  <div><strong>{{ t('layout.loading') }}:</strong> {{ registerStore.loading ? t('layout.yes') : t('layout.no') }}</div>
                  <div><strong>{{ t('layout.registerCount') }}:</strong> {{ registerStore.registers?.length || 0 }}</div>
                  <div><strong>{{ t('layout.openRegisters') }}:</strong> {{ registerStore.getOpenRegisters?.length || 0 }}</div>
                </div>
              </q-card>
            </div>

            <!-- Cookie Information -->
            <div class="col-12">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.cookies') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.totalCookies') }}:</strong> {{ document?.cookie ? document?.cookie.split(';').length : 0 }}
                  </div>
                  <div><strong>{{ t('layout.cookieData') }}:</strong></div>
                  <div class="bg-grey-2 q-pa-sm rounded font-mono text-caption"
                       style="max-height: 100px; overflow-y: auto;">
                    {{ document?.cookie || t('layout.noCookiesFound') }}
                  </div>
                </div>
              </q-card>
            </div>

            <!-- Server Response -->
            <div class="col-12">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.serverResponse') }}</div>
                <div class="text-body2">
                  <div><strong>{{ t('layout.origin') }}:</strong> {{ sessionInfo.origin || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.cookiesReceived') }}:</strong> {{ sessionInfo.cookies_received || t('layout.none') }}</div>
                  <div><strong>{{ t('layout.lastUpdated') }}:</strong> {{ sessionInfo.last_updated || t('layout.none') }}</div>
                </div>
              </q-card>
            </div>

            <!-- Runtime environment (build-time; dev/debug builds only) -->
            <div v-if="isDevelopment" class="col-12">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.runtimeEnvironment') }}</div>
                <div class="text-caption text-grey-7 q-mb-sm">{{ t('layout.runtimeEnvironmentHint') }}</div>
                <div class="text-body2">
                  <div
                    v-for="row in runtimeEnvRows"
                    :key="row.key"
                    class="row q-col-gutter-sm q-mb-xs"
                  >
                    <div class="col-12 col-sm-5 text-weight-medium font-mono">{{ row.key }}</div>
                    <div class="col-12 col-sm-7 font-mono">{{ row.value }}</div>
                  </div>
                </div>
              </q-card>
            </div>

            <div v-if="isDevelopment && dejavooTopNativeRows.length" class="col-12">
              <q-card flat bordered class="q-pa-md session-card">
                <div class="text-subtitle2 text-weight-medium q-mb-sm">{{ t('layout.dejavooTopNativeDiagnostics') }}</div>
                <div class="text-caption text-grey-7 q-mb-sm">{{ t('layout.dejavooTopNativeDiagnosticsHint') }}</div>
                <div class="text-body2">
                  <div
                    v-for="row in dejavooTopNativeRows"
                    :key="row.key"
                    class="row q-col-gutter-sm q-mb-xs"
                  >
                    <div class="col-12 col-sm-5 text-weight-medium font-mono">{{ row.key }}</div>
                    <div class="col-12 col-sm-7 font-mono">{{ row.value }}</div>
                  </div>
                </div>
              </q-card>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat :label="t('layout.refreshSession')" color="primary" @click="refreshSessionInfo" />
          <q-btn flat :label="t('layout.refreshRegisters')" color="secondary" @click="refreshRegisterData" />
          <q-btn flat :label="t('layout.close')" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <IphoneTopMarketingSplash v-model="showIphoneTopMarketingSplash" />
    <IphoneTopGlobalPreparingOverlay />
    <PostSaleReceiptChoicesDialog />
  </q-layout>
</template>

<script setup>
import PostSaleReceiptChoicesDialog from '@/components/PostSaleReceiptChoicesDialog.vue'
import IphoneTopMarketingSplash from '@/components/payments/IphoneTopMarketingSplash.vue'
import IphoneTopGlobalPreparingOverlay from '@/components/payments/IphoneTopGlobalPreparingOverlay.vue'
import { useIphoneTopEligibility } from '@/composables/useIphoneTopEligibility.js'
import { useIphoneTopMarketingAck } from '@/composables/useIphoneTopMarketingAck.js'
import { ref, onMounted, onUnmounted, watch, computed, provide, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LocationSelector from '@/components/LocationSelector.vue'
import UserMenu from '@/components/UserMenu.vue'
import { api } from '@/boot/axios'
import { customerDisplayService } from '@/services'
import { useTerminalStore } from '@/stores/terminal'
import { useRegisterStore } from '@/stores/register'
import { useLocationStore } from '@/stores/location'
import { useBusinessStore } from '@/stores/business'
import { useOfflineStatus } from '@/composables/useOfflineStatus'
import { getRuntimeEnvSnapshot } from '@/config/runtimeEnv'
import { dejavooTopNativeDiagnosticsRows } from '@/services/dejavooTopCheckout.js'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import { NAV_LINKS } from '@/config/navLinks'
import { useOrderAheadQueueStore } from '@/stores/orderAheadQueue'
import { useStoreOrderQueueStore } from '@/stores/storeOrderQueue'
import OrderAheadQueueDrawerPanel from '@/components/orderAhead/OrderAheadQueueDrawerPanel.vue'
import { useAutoUpdate, electronAutoUpdateKey } from '@/composables/useAutoUpdate'
import { userHasAbility } from '@/utils/permissions'
import { isAnyTabsFeatureActive } from '@/utils/tabFeatureHelpers'
import { isBookingFeatureActive } from '@/utils/bookingFeatureHelpers'
import { isEventsFeatureActive } from '@/utils/eventsFeatureHelpers'
import { isLeadsFeatureActive } from '@/utils/leadsFeatureHelpers'
import { isInvoicesFeatureActive, isProjectsFeatureActive } from '@/utils/projectFeatureHelpers'
import { schedulingUiEnabled } from '@/config/schedulingUi'

const { t } = useI18n()
const $q = useQuasar()
const { showCommunications, refreshWizardState } = useIphoneTopEligibility()
const { hasSeenMarketing } = useIphoneTopMarketingAck()
const showIphoneTopMarketingSplash = ref(false)
let iphoneTopSplashTimer = null

// reactive() unwraps nested refs so templates/inject consumers read plain values
const electronAutoUpdate = reactive(useAutoUpdate())
provide(electronAutoUpdateKey, electronAutoUpdate)

const isElectronDesktop = computed(
  () => typeof window !== 'undefined' && !!window?.electronAPI
)

const showElectronUpdateAffordance = computed(() => {
  if (!isElectronDesktop.value) return false
  const phase = electronAutoUpdate.updatePhase
  return ['available', 'downloading', 'downloaded'].includes(phase)
})
// import Intercom from '@intercom/messenger-js-sdk'

const router = useRouter()
const route = useRoute()

const compactToolbar = computed(() => $q.screen.lt.md)
const orderAheadDrawerWidth = computed(() => Math.min(440, $q.screen.width))

/**
 * Match Quasar breakpoint `lg` (default ~1440px): below this, the drawer is overlay-only and
 * does not reserve horizontal space-avoids cramped POS layouts at tablet / small-laptop widths.
 * At and above this width, the drawer can persist beside the content when opened.
 *
 * Keeps breakpoint and default-open state aligned (`$q.screen.width >= DRAWER_SIDEBAR_MIN_WIDTH` ≈ `!$q.screen.lt.lg`).
 */
const DRAWER_SIDEBAR_MIN_WIDTH = 1440

const authStore = useAuthStore()
const orderAheadStore = useOrderAheadQueueStore()
const storeOrderQueueStore = useStoreOrderQueueStore()
const orderAheadDrawerOpen = ref(false)

const canOrderAheadQueue = computed(() => {
  const abilities = authStore.user?.abilities || []
  const hasAbility = abilities.includes('*') || abilities.includes('pos.sell')
  return hasAbility && !!businessStore.info?.order_ahead_enabled
})

const canStoreOrderQueue = computed(() => {
  const abilities = authStore.user?.abilities || []
  const hasAbility = abilities.includes('*') || abilities.includes('pos.sell')
  return hasAbility && !!businessStore.info?.store_enabled
})
const terminalStore = useTerminalStore()
const registerStore = useRegisterStore()

const registerChipTitle = computed(() => {
  if (!registerStore.currentRegister?.name) return t('layout.registerManageTitle')
  return `${registerStore.currentRegister.name} - ${t('layout.registerManageTitle')}`
})

const locationStore = useLocationStore()
const businessStore = useBusinessStore()
const { isOffline, effectiveType } = useOfflineStatus()

// Narrow viewports (~< lg): nav closed until opened; wider layouts start with the sidebar visible.
const leftDrawerOpen = ref($q.screen.width >= DRAWER_SIDEBAR_MIN_WIDTH)
const sessionId = ref('')
const showSessionDialog = ref(false)
const sessionInfo = ref({})
const isDevelopment = import.meta.env.NODE_ENV === 'development' || !!import.meta.env.QUASAR_DEBUG
const runtimeEnvRows = getRuntimeEnvSnapshot()
const dejavooTopNativeRows = ref([])
const locationRenderVersion = ref(0)
let terminalRefreshInterval = null
let registerRefreshInterval = null
let locationChangedHandler = null

/** True when auth + optional location still match a snapshot (guards login/logout races). */
const isActiveLayoutSession = (locationId = null) => {
  if (!authStore.isAuthenticated) return false
  if (locationId == null) {
    return !!locationStore.getCurrentLocation?.id
  }
  return locationStore.getCurrentLocation?.id === locationId
}

const routeViewKey = computed(() => {
  // Centralized refresh: when location changes, remount the active route component.
  return `${route.fullPath}::loc:${locationRenderVersion.value}`
})

// Function to fetch session info
const fetchSessionInfo = async () => {
  try {
    const response = await api.get('/session-info')
    sessionId.value = response.data.session_id.substring(0, 8) + '...'

    // Populate comprehensive session info
    sessionInfo.value = {
      ...response.data,
      user: authStore.user,
      last_updated: new Date().toLocaleString()
    }
  } catch (error) {
    console.error('Failed to fetch session info:', error)
    sessionId.value = 'Error'
    sessionInfo.value = {
      session_id: 'Error',
      user: authStore.user,
      last_updated: new Date().toLocaleString()
    }
  }
}

async function fetchDejavooTopNativePanel () {
  try {
    dejavooTopNativeRows.value = await dejavooTopNativeDiagnosticsRows()
  } catch {
    dejavooTopNativeRows.value = []
  }
}

// Function to refresh session info (for debugging)
const refreshSessionInfo = () => {
  fetchSessionInfo()
  fetchDejavooTopNativePanel()
}

// Function to refresh terminal connection status
const refreshTerminalConnection = async () => {
  if (!supportsTerminalStatus.value) return
  // Get terminal status for the current user's register instead of hardcoded terminal ID
  if (registerStore.currentRegister?.id) {
    try {
      await registerStore.getTerminalStatusForRegister(registerStore.currentRegister.id)
    } catch (error) {
      console.error('Failed to get terminal status for current register:', error)
    }
  } else {
    // Fallback to loading current user register first
    try {
      await registerStore.loadCurrentUserRegister()
      if (registerStore.currentRegister?.id) {
        await registerStore.getTerminalStatusForRegister(registerStore.currentRegister.id)
      }
    } catch (error) {
      console.error('Failed to load current register or get terminal status:', error)
    }
  }
}

// Register toolbar: open POS when register is open elsewhere; from POS go to management to close
const onRegisterToolbarClick = () => {
  if (route.name === 'sales-register') {
    router.push({ name: 'registers' })
    return
  }
  if (registerStore.isCurrentRegisterOpen) {
    router.push({ name: 'sales-register' })
    return
  }
  router.push({ name: 'registers' })
}

// Function to refresh register data in the session dialog
const refreshRegisterData = async () => {
  await registerStore.loadOverview()
}

function installIosTopLifecycleIfNeeded () {
  if (!authStore.isAuthenticated) {
    return
  }
  void import('@/services/iosTapToPayLifecycle.js').then((m) => {
    m.installIosTapToPayAppLifecycle()
  })
}

async function scheduleIphoneTopMarketingSplash () {
  if (iphoneTopSplashTimer != null) {
    globalThis.clearTimeout(iphoneTopSplashTimer)
    iphoneTopSplashTimer = null
  }
  if (!authStore.isAuthenticated) {
    showIphoneTopMarketingSplash.value = false
    return
  }
  iphoneTopSplashTimer = globalThis.setTimeout(async () => {
    iphoneTopSplashTimer = null
    try {
      const { isIosTopTapToPaySaleInFlight } = await import('@/services/DejavooTop.ios.js')
      if (isIosTopTapToPaySaleInFlight()) {
        return
      }
      await refreshWizardState()
      if (!showCommunications.value || hasSeenMarketing()) {
        return
      }
      showIphoneTopMarketingSplash.value = true
    } catch {
      /* best-effort */
    }
  }, 1000)
}

// Initialize banners when component mounts
onMounted(async () => {
  installIosTopLifecycleIfNeeded()
  locationChangedHandler = () => {
    locationRenderVersion.value += 1
  }
  locationStore.onLocationChanged(locationChangedHandler)

  // Initialize location store first - this is critical for all other operations
  if (authStore.isAuthenticated) {
    try {
      await locationStore.fetchLocations()
      if (!isActiveLayoutSession()) return

      await locationStore.initializeCurrentLocation()
      const locationId = locationStore.getCurrentLocation?.id
      if (!locationId || !isActiveLayoutSession(locationId)) return
    } catch (error) {
      console.error('Failed to initialize location store:', error)
      // Don't proceed with other operations if location fails
      return
    }
  }

  // Now load terminals since we have location
  if (isActiveLayoutSession()) {
    const locationId = locationStore.getCurrentLocation?.id
    try {
      await terminalStore.loadTerminalsForLocation(locationId)
    } catch (error) {
      console.error('Failed to load terminals:', error)
    }
  }

  // Check terminal connection on mount (only if supported)
  // Get terminal status for current user's register instead of hardcoded terminal ID
  if (supportsTerminalStatus.value && registerStore.currentRegister?.id) {
    try {
      await registerStore.getTerminalStatusForRegister(registerStore.currentRegister.id)
    } catch (error) {
      console.error('Failed to get terminal status for current register:', error)
    }
  }

  // Set up periodic refresh for terminal status
  terminalRefreshInterval = setInterval(async () => {
    if (!supportsTerminalStatus.value) return
    if (registerStore.currentRegister?.id) {
      try {
        await registerStore.getTerminalStatusForRegister(registerStore.currentRegister.id)
      } catch (error) {
        console.error('Failed to refresh terminal status:', error)
      }
    }
  }, 30000) // Refresh every 30 seconds

  // Load current register if user is authenticated
  if (isActiveLayoutSession()) {
    const locationId = locationStore.getCurrentLocation?.id
    try {
      await registerStore.loadOverview()
      if (!isActiveLayoutSession(locationId)) return

      try {
        await customerDisplayService.syncCustomerDisplayNativeFromStorage()
      } catch {
        // ignore
      }

      // Set up periodic refresh for register data
      registerRefreshInterval = setInterval(async () => {
        if (!isActiveLayoutSession()) return
        try {
          await registerStore.loadOverview()
        } catch (error) {
          console.error('Failed to refresh register data:', error)
        }
      }, 60000) // Refresh every minute
    } catch (error) {
      console.error('Failed to load registers:', error)
    }

    /*Intercom({
      app_id: 'xvdjpdxq',
      user_id: authStore.user.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
      name: authStore.user.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
      email: authStore.user.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
      created_at: authStore.user.created_at, // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
    });*/
  }

  if (isDevelopment) {
    fetchSessionInfo()
  }

  if (authStore.isAuthenticated) {
    void scheduleIphoneTopMarketingSplash()
  }
})

// Watch for authentication changes to load/clear register data
watch(() => authStore.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    installIosTopLifecycleIfNeeded()
    void scheduleIphoneTopMarketingSplash()
    try {
      await locationStore.fetchLocations()
      if (!isActiveLayoutSession()) return

      await locationStore.initializeCurrentLocation()
      const locationId = locationStore.getCurrentLocation?.id
      if (!locationId || !isActiveLayoutSession(locationId)) return

      await terminalStore.loadTerminalsForLocation(locationId)
      if (!isActiveLayoutSession(locationId)) return

      await registerStore.loadOverview()
      if (!isActiveLayoutSession(locationId)) return

      try {
        await customerDisplayService.syncCustomerDisplayNativeFromStorage()
      } catch {
        // ignore
      }

      // Set up periodic refresh for register data
      if (registerRefreshInterval) {
        clearInterval(registerRefreshInterval)
      }
      registerRefreshInterval = setInterval(async () => {
        if (!isActiveLayoutSession()) return
        try {
          await registerStore.loadOverview()
        } catch (error) {
          console.error('Failed to refresh register data:', error)
        }
      }, 60000) // Refresh every minute
    } catch (error) {
      if (!isActiveLayoutSession()) return
      console.error('Failed to load data after auth change:', error)
    }
  } else {
    showIphoneTopMarketingSplash.value = false
    if (iphoneTopSplashTimer != null) {
      globalThis.clearTimeout(iphoneTopSplashTimer)
      iphoneTopSplashTimer = null
    }
    void import('@/services/iosTopCheckoutReadiness.js').then((m) => {
      m.invalidateIosTopCheckoutReadiness('logout')
    })
    void import('@/stores/iphoneTopUi.js').then(({ useIphoneTopUiStore }) => {
      useIphoneTopUiStore().resetPreparing()
    })
    // Clear session-scoped layout data when user logs out (setup stores have no $reset).
    registerStore.resetOnLogout()
    locationStore.resetOnLogout()

    // Clear refresh interval
    if (registerRefreshInterval) {
      clearInterval(registerRefreshInterval)
      registerRefreshInterval = null
    }
  }
})

// Watch for location changes to reload terminals
watch(() => locationStore.getCurrentLocation, async (newLocation) => {
  const locationId = newLocation?.id
  if (!locationId || !isActiveLayoutSession(locationId)) return
  // Sales register page runs its own tiered bootstrap on mount.
  if (route.name === 'sales-register') return

  try {
    await terminalStore.loadTerminalsForLocation(locationId)
    if (!isActiveLayoutSession(locationId)) return

    await registerStore.loadOverview()
  } catch (error) {
    if (!isActiveLayoutSession(locationId)) return
    console.error('Failed to load data after location change:', error)
  }
})

// Watch for current register changes to update terminal status
watch(() => registerStore.currentRegister, async (newRegister) => {
  if (newRegister?.id && authStore.isAuthenticated && supportsTerminalStatus.value) {
    try {
      await registerStore.getTerminalStatusForRegister(newRegister.id)
    } catch (error) {
      console.error('Failed to get terminal status for new register:', error)
    }
  }
}, { immediate: false })

// Watch for current register ID changes to reload terminal health
watch(() => registerStore.currentRegister?.id, async (newRegisterId) => {
  if (newRegisterId) {
    // Reload terminal status when current register changes
    await refreshTerminalConnection()
  }
})

// Watch for session dialog to refresh session info when opened
watch(showSessionDialog, async (isOpen) => {
  if (isOpen) {
    await Promise.all([fetchSessionInfo(), fetchDejavooTopNativePanel()])
  }
})

watch(
  canOrderAheadQueue,
  (ok) => {
    if (ok) {
      orderAheadStore.subscribe()
    } else {
      orderAheadStore.unsubscribe()
    }
  },
  { immediate: true }
)

watch(
  canStoreOrderQueue,
  (ok) => {
    if (ok) {
      storeOrderQueueStore.subscribe()
    } else {
      storeOrderQueueStore.unsubscribe()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  if (iphoneTopSplashTimer != null) {
    globalThis.clearTimeout(iphoneTopSplashTimer)
    iphoneTopSplashTimer = null
  }
  if (locationChangedHandler) {
    locationStore.offLocationChanged(locationChangedHandler)
    locationChangedHandler = null
  }
  if (terminalRefreshInterval) {
    clearInterval(terminalRefreshInterval)
  }
  if (registerRefreshInterval) {
    clearInterval(registerRefreshInterval)
  }
  orderAheadStore.unsubscribe()
  storeOrderQueueStore.unsubscribe()
})

const rawLinks = NAV_LINKS

const links = computed(() => {
  const user = authStore.user
  const tabsEnabled = isAnyTabsFeatureActive(businessStore.info)
  const rentalsEnabled = businessStore.info?.rentals_enabled ?? false
  const bookingEnabled = isBookingFeatureActive(businessStore.info)
  const invoicesEnabled = isInvoicesFeatureActive(businessStore.info)
  const projectsEnabled = isProjectsFeatureActive(businessStore.info)
  const eventsEnabled = isEventsFeatureActive(businessStore.info)
  const leadsEnabled = isLeadsFeatureActive(businessStore.info)
  return rawLinks.filter(l => {
    if (l.link === '/tabs' && !tabsEnabled) return false
    if (l.link === '/rentals' && !rentalsEnabled) return false
    if (l.link === '/booking' && !bookingEnabled) return false
    if (l.link === '/invoices' && !invoicesEnabled) return false
    if (l.link === '/projects' && !projectsEnabled) return false
    if (l.link === '/events' && !eventsEnabled) return false
    if (l.link === '/leads' && !leadsEnabled) return false
    if (l.link === '/scheduling' && (bookingEnabled || !schedulingUiEnabled)) return false
    if (!l.ability && !l.abilityAny) return true
    if (l.ability && userHasAbility(user, l.ability)) return true
    if (Array.isArray(l.abilityAny)) return l.abilityAny.some(a => userHasAbility(user, a))
    return false
  })
})

const isActive = (link) => {
  if (link === '/settings') return route.path.startsWith('/settings')
  return route.path === link
}

const handleLogout = async () => {
  const logoutPromise = authStore.logout()
  await router.replace('/login')
  await logoutPromise
}

// Computed properties for terminal status display
const getTerminalStatusColor = computed(() => {
  if (!supportsTerminalStatus.value) return 'grey'
  if (registerStore.terminalStatusLoading) {
    return 'warning'
  }
  const currentRegisterId = registerStore.currentRegister?.id
  if (!currentRegisterId) return 'grey'

  const registerWithTerminal = registerStore.registersWithTerminals.find(r => r.register.id === currentRegisterId)
  if (!registerWithTerminal) return 'grey'

  return registerWithTerminal.terminal_healthy ? 'positive' : 'negative'
})

const getTerminalStatusLabel = computed(() => {
  if (!supportsTerminalStatus.value) {
    return t('layout.terminalNoStatus')
  }
  if (registerStore.terminalStatusLoading) {
    return t('layout.terminalLoading')
  }
  const currentRegisterId = registerStore.currentRegister?.id
  if (!currentRegisterId) return t('layout.terminalNoRegister')

  const registerWithTerminal = registerStore.registersWithTerminals.find(r => r.register.id === currentRegisterId)
  if (!registerWithTerminal) return t('layout.terminalNoTerminal')

  return registerWithTerminal.terminal_healthy ? t('layout.terminalReady') : t('layout.terminalIssue')
})

const getTerminalStatusIcon = computed(() => {
  if (!supportsTerminalStatus.value) return 'help'
  if (registerStore.terminalStatusLoading) {
    return 'refresh'
  }
  const currentRegisterId = registerStore.currentRegister?.id
  if (!currentRegisterId) return 'help'

  const registerWithTerminal = registerStore.registersWithTerminals.find(r => r.register.id === currentRegisterId)
  if (!registerWithTerminal) return 'help'

  return registerWithTerminal.terminal_healthy ? 'check_circle' : 'error'
})

// Support check: only show/poll terminal status if gateway supports ping/status (not mxmerchant)
const supportsTerminalStatus = computed(() => {
  const currentRegisterId = registerStore.currentRegister?.id
  if (!currentRegisterId) return false
  const reg = registerStore.registersWithTerminals.find(r => r.register.id === currentRegisterId)
  const gateway = (reg?.terminal?.gateway || '').toLowerCase()
  return gateway && gateway !== 'mxmerchant'
})
</script>

<style lang="scss">
@use "sass:map";
@use "sass:color";
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');
@import '../css/variables.scss';
@import '../css/mixins.scss';
@import '../css/utilities.scss';

.main-layout {
  // Let the tokenized global background show through (body already uses .bg-gradient)
  background: transparent;
}

.toolbar-brand--compact .brand-text {
  font-size: 1.35rem;
}

.brand-text {
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  letter-spacing: -0.02em;
  font-size: 2rem;
  line-height: 1.1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  background: linear-gradient(45deg, $brand-primary, color.adjust($brand-primary, $lightness: 10%));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.header-glass {
  @include glass-effect;
  border-bottom: 1px solid $glass-border;
  color: $brand-dark;
  box-shadow: $shadow-sm;
}

.pickup-drawer {
  max-width: 100vw;
}

.drawer-glass {
  @include glass-effect;
  border-right: 1px solid $glass-border;
  color: $brand-dark;
  box-shadow: $shadow-lg;

  .q-list {
    padding: $spacing-sm 0;
  }
}

.nav-link {
  border-radius: 8px;
  margin: 4px 12px;
  color: rgba(0, 0, 0, 0.8);
  transition: all 0.3s ease;
  height: 48px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
    border-radius: 8px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  // Keyboard accessibility (visible focus on glass)
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba($brand-primary, 0.25);
    background: rgba(255, 255, 255, 0.55);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.5);
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    &::before {
      opacity: 1;
    }
  }

  .q-icon {
    color: rgba(0, 0, 0, 0.8);
    font-size: 24px;
  }

  &.q-item--active {
    background: rgba($brand-primary, 0.10) !important;
    color: rgba($brand-dark, 0.95);
    font-weight: $font-weight-medium;
    box-shadow: $shadow-sm;

    &::before {
      opacity: 1;
      background: linear-gradient(45deg, rgba($brand-primary, 0.10), rgba($brand-secondary, 0.06));
    }

    .q-icon {
      color: rgba($brand-dark, 0.95);
    }
  }

  // Subtle active indicator bar (tokenized)
  &.q-item--active::after {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 2px;
    background: linear-gradient(180deg, $brand-primary, $brand-secondary);
    opacity: 0.9;
  }
}

.separator-glass {
  opacity: 0.3;
  margin: 8px 0;
  height: 1px;
  background: linear-gradient(90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0) 100%
  );
}

.q-item-label.header {
  color: rgba(0, 0, 0, 0.8);
  font-weight: 500;
  padding: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.terminal-status {
  .q-chip {
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}

.current-register {
  .current-register-chip {
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.q-table__bottom .q-field--borderless.q-field--dense .q-field__control {
  padding-left: 8px !important;
}

.session-debug {
  .q-chip {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}

// Session dialog styling
.session-info-dialog {
  .q-card {
    border-radius: $border-radius-lg;
    box-shadow: $shadow-xl;
  }

  .session-card {
    border-radius: $border-radius-md;
    border: 1px solid $glass-border;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);

    .text-subtitle2 {
      color: $brand-dark;
      font-weight: 500;
    }

    .text-body2 {
      color: rgba(0, 0, 0, 0.8);
      line-height: 1.5;
    }
  }

  .font-mono {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.85rem;
  }

  .bg-grey-2 {
    background-color: rgba(0, 0, 0, 0.05) !important;
  }
}

// Responsive override for session dialog on small screens
@media (max-width: 768px) {
  .session-info-dialog {
    .q-card {
      min-width: auto !important;
      width: 100% !important;
      border-radius: 0 !important;
    }
  }
}
</style>
